import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface BaseEntityCardQuickChatProps {
  visible: boolean;
  onSubmit: (message: string) => void;
  onClose: () => void;
  placeholder?: string;
}

/**
 * Quick chat input for entity cards
 * Ephemeral chat input that appears when user wants to ask about an entity
 */
export function BaseEntityCardQuickChat({
  visible,
  onSubmit,
  onClose,
  placeholder = "Ask about this...",
}: BaseEntityCardQuickChatProps) {
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus when visible
  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setMessage(""); // Clear on close
    }
  }, [visible]);

  // Handle escape to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [visible, onClose]);

  // Handle click outside to close
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is inside the chat input container
      if (containerRef.current?.contains(target)) {
        return;
      }

      // Close on any other click (canvas, node, toolbar, etc.)
      onClose();
    };

    // Small delay to prevent immediate close when opening
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [visible, onClose]);

  const handleSubmit = useCallback(() => {
    if (!message.trim()) return;
    onSubmit(message.trim());
    setMessage("");
    onClose();
  }, [message, onSubmit, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const hasContent = message.trim().length > 0;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="w-full relative z-50"
        >
          {/* Compact composer register — the real message input's kin:
              solid card surface, hairline border, the card corner, one
              elevation, rust send. */}
          <div className="bg-n-00 border border-border rounded-lg shadow-elev-2 overflow-hidden">
            {/* Input with send button */}
            <div className="flex items-center gap-2 px-2.5 py-1.5">
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />

              {/* Send button */}
              <button
                onClick={handleSubmit}
                disabled={!hasContent}
                className={cn(
                  "flex-shrink-0 h-6 w-6 rounded-md",
                  "flex items-center justify-center",
                  "transition-colors duration-150",
                  hasContent
                    ? "bg-rust text-n-00 hover:bg-rust/90 cursor-pointer"
                    : "border border-border text-muted-foreground cursor-default",
                )}
              >
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
